import { supabase } from "./client";
import { getCurrentEmpresaId } from "./auth-utils";
import { mapPaymentToOrderStatus } from "@/utils/statusConstants";
import { checkDatabaseHealth } from "./config";

export interface ReceitaRow {
  id: string;
  empresa_id: string;
  order_id?: string;
  order_code: string;
  customer_name: string;
  description?: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
  created_at: string;
  updated_at: string;
}

// Função para buscar receitas
export async function listReceitas(): Promise<ReceitaRow[]> {
  try {
    console.log("Buscando receitas...");
    
    // Obter empresa_id do usuário logado
    const { data: userEmpresa } = await supabase
      .from("user_empresas")
      .select("empresa_id")
      .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
      .maybeSingle();
    
    if (!userEmpresa?.empresa_id) {
      console.error("Usuário não tem empresa associada");
      return [];
    }
    
    // Verificar se o banco está funcionando
    const isDbWorking = await checkDatabaseHealth();
    
    if (!isDbWorking) {
      console.log("Banco não está funcionando");
      return [];
    }

    const { data, error } = await (supabase
      .from("atelie_receitas" as any)
      .select("*")
      .eq("empresa_id", userEmpresa.empresa_id)
      .order("payment_date", { ascending: false })
      .limit(100) as any);

    if (error) {
      console.error("Erro ao buscar receitas:", error);
      return [];
    }

    console.log("Receitas encontradas:", data?.length || 0);
    return data ?? [];
  } catch (e: unknown) {
    console.error("Erro ao buscar receitas:", e);
    return [];
  }
}

// Função para buscar receita por código do pedido
export async function getReceitaByOrderCode(orderCode: string): Promise<ReceitaRow | null> {
  try {
    console.log("Buscando receita por código do pedido:", orderCode);
    
    // Obter empresa_id do usuário logado
    const { data: userEmpresa } = await supabase
      .from("user_empresas")
      .select("empresa_id")
      .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
      .maybeSingle();
    
    if (!userEmpresa?.empresa_id) {
      console.error("Usuário não tem empresa associada");
      return null;
    }
    
    // Verificar se o banco está funcionando
    const isDbWorking = await checkDatabaseHealth();
    
    if (!isDbWorking) {
      console.log("Banco não está funcionando");
      return null;
    }

    const { data, error } = await (supabase
      .from("atelie_receitas" as any)
      .select("*")
      .eq("order_code", orderCode)
      .eq("empresa_id", userEmpresa.empresa_id)
      .order("updated_at", { ascending: false })
      .limit(1) as any);

    if (error) {
      console.error("Erro ao buscar receita:", error);
      return null;
    }

  const receita = Array.isArray(data) ? data[0] : data;
  console.log("Receita encontrada:", receita || null);
  return (receita ?? null) as ReceitaRow | null;
  } catch (e: unknown) {
    console.error("Erro ao buscar receita:", e);
    return null;
  }
}

// Função para criar receita
export async function createReceita(input: {
  order_code: string;
  customer_name: string;
  description?: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status?: string;
}): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    console.log("Criando receita:", input);
    
    // Obter empresa_id do usuário logado
    const empresa_id = await getCurrentEmpresaId();
    
    const { data, error } = await (supabase
      .from("atelie_receitas" as any)
      .insert({
        empresa_id,
        order_code: input.order_code,
        customer_name: input.customer_name,
        description: input.description ?? null,
        amount: input.amount,
        payment_method: input.payment_method,
        payment_date: input.payment_date,
        status: input.status ?? "Pago"
      })
      .select()
      .maybeSingle() as any);

    if (error) {
      console.error("Erro ao criar receita:", error);
      return { ok: false, error: error.message };
    }

    console.log("Receita criada com sucesso:", data);
    return { ok: true, id: data.id };
  } catch (e: unknown) {
    console.error("Erro ao criar receita:", e);
    return { ok: false, error: e instanceof Error ? e.message : "Erro ao criar receita" };
  }
}

// Função para atualizar receita
export async function updateReceita(
  id: string,
  updates: Partial<{
    description: string;
    amount: number;
    payment_method: string;
    payment_date: string;
    status: string;
  }>
): Promise<{ ok: boolean; data?: ReceitaRow; error?: string }> {
  try {
    console.log(`Atualizando receita ${id}:`, updates);
    
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    const { data, error } = await (supabase
      .from("atelie_receitas" as any)
      .update(updateData)
      .eq("id", id)
      .select()
      .maybeSingle() as any);

    if (error) {
      console.error("Erro ao atualizar receita:", error);
      return { ok: false, error: error.message };
    }

    console.log("Receita atualizada com sucesso:", data);
    return { ok: true, data: data as ReceitaRow };
  } catch (e: unknown) {
    console.error("Erro ao atualizar receita:", e);
    return { ok: false, error: e instanceof Error ? e.message : "Erro ao atualizar receita" };
  }
}

// Função para deletar receita
export async function deleteReceita(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    console.log("Deletando receita:", id);
    
    const { error } = await (supabase
      .from("atelie_receitas" as any)
      .delete()
      .eq("id", id) as any);

    if (error) {
      console.error("Erro ao deletar receita:", error);
      return { ok: false, error: error.message };
    }

    console.log("Receita deletada com sucesso");
    return { ok: true };
  } catch (e: unknown) {
    console.error("Erro ao deletar receita:", e);
    return { ok: false, error: e instanceof Error ? e.message : "Erro ao deletar receita" };
  }
}

// Função para atualizar status de pagamento
export async function updatePaymentStatus(
  orderCode: string,
  status: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    console.log(`Atualizando status de pagamento para ${orderCode}:`, status);
    
    // Buscar o pedido para obter o valor total e customer_name
    const { data: order, error: orderError } = await (supabase
      .from("atelie_orders" as any)
      .select("id, value, paid, empresa_id, customer_name, discount, discount_type")
      .eq("code", orderCode)
      .maybeSingle() as any);

    if (orderError || !order) {
      console.error("Erro ao buscar pedido:", orderError);
      return { ok: false, error: "Pedido não encontrado" };
    }

    // Calcular valor com desconto
    const subtotal = order.value;
    const discount = order.discount || 0;
    const discountType = order.discount_type || 'fixed';
    const discountAmount = discountType === 'percentage'
      ? (subtotal * discount / 100)
      : discount;
    const totalValueWithDiscount = Math.max(0, subtotal - discountAmount);

    // Calcular novo valor pago baseado no status
    let newPaidValue = 0;

    if (status === 'pago') {
      newPaidValue = totalValueWithDiscount; // Pago = valor total com desconto
    } else if (status === 'pendente') {
      newPaidValue = 0; // Pendente = nada pago
    } else if (status === 'parcial') {
      // Manter valor atual ou metade se for 0
      newPaidValue = order.paid > 0 ? order.paid : totalValueWithDiscount / 2;
    }
    
    // IMPORTANTE: Criar/Atualizar registro na tabela RECEITAS, não apenas order.paid
    // Buscar se já existe receita para este pedido
    const { data: existingReceita } = await (supabase
      .from("atelie_receitas" as any)
      .select("id")
      .eq("order_code", orderCode)
      .maybeSingle() as any);

    if (existingReceita) {
      // Atualizar receita existente COM STATUS
      const { error: updateReceitaError } = await (supabase
        .from("atelie_receitas" as any)
        .update({ 
          amount: newPaidValue,
          status: newPaidValue > 0 ? "pago" : "pendente", // Atualizar status baseado no valor
          updated_at: new Date().toISOString()
        })
        .eq("order_code", orderCode) as any);

      if (updateReceitaError) {
        console.error("Erro ao atualizar receita:", updateReceitaError);
        return { ok: false, error: updateReceitaError.message };
      }
    } else {
      // Criar nova receita com todos os campos obrigatórios
      const receitaData = {
        order_code: orderCode,
        customer_name: order.customer_name || "Sem nome",
        description: `Pagamento do pedido ${orderCode}`, // Campo obrigatório
        amount: newPaidValue,
        payment_method: "Dinheiro", // Método padrão
        payment_date: new Date().toISOString().split('T')[0], // Data de hoje
        status: newPaidValue > 0 ? "pago" : "pendente", // Status baseado no valor
        empresa_id: order.empresa_id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      console.log("Tentando criar receita com dados:", receitaData);
      
      const { error: createReceitaError } = await (supabase
        .from("atelie_receitas" as any)
        .insert(receitaData) as any);

      if (createReceitaError) {
        console.error("Erro detalhado ao criar receita:", JSON.stringify(createReceitaError, null, 2));
        // Não falhar aqui, apenas logar
      } else {
        console.log("Receita criada com sucesso!");
      }
    }
    
    // Atualizar também order.paid para compatibilidade (DEPRECATED - não usar mais)
    const { error: updateError } = await (supabase
      .from("atelie_orders" as any)
      .update({ 
        paid: newPaidValue,
        updated_at: new Date().toISOString()
      })
      .eq("code", orderCode) as any);

    if (updateError) {
      console.error("Erro ao atualizar order.paid:", updateError);
      // Não retornar erro aqui, pois a receita já foi atualizada
    }

    console.log("Status de pagamento atualizado com sucesso");
    return { ok: true };
  } catch (e: unknown) {
    console.error("Erro ao atualizar status de pagamento:", e);
    return { ok: false, error: (e as any)?.message || "Erro ao atualizar status de pagamento" };
  }
}